import { X, Minus, Plus, ShoppingBag, Truck, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/store/cartStore";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { formatPrice } from "@/lib/utils";
import { useLanguage } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { fetchShopifyCollections, type ShopifyCollection } from "@/lib/shopify";
import { CartRecommendations } from "@/components/CartRecommendations";
import { MobileResizableCart } from "@/components/MobileResizableCart";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Flag imports
import flagIsrael from "@/assets/flag-israel.png";
import flagUSA from "@/assets/flag-usa.png";
import flagPalestine from "@/assets/flag-palestine.png";
import flagRussia from "@/assets/flag-russia.png";

// Font preview images
import fontA from "@/assets/fonts/font-a.webp";
import fontB from "@/assets/fonts/font-b.webp";
import fontC from "@/assets/fonts/font-c.webp";
import fontD from "@/assets/fonts/font-d.webp";
import fontE from "@/assets/fonts/font-e.webp";
import fontF from "@/assets/fonts/font-f.webp";

// Icon images
import iconHeart from "@/assets/icons/icon-heart.png";
import iconCrown from "@/assets/icons/icon-crown.png";
import iconStar from "@/assets/icons/icon-star.png";
import iconDavid from "@/assets/icons/icon-david.png";
import iconAmpersand from "@/assets/icons/icon-ampersand.png";

// Font and icon maps for cart display
const FONT_IMAGES: Record<string, string> = {
  "font-a": fontA,
  "font-b": fontB,
  "font-c": fontC,
  "font-d": fontD,
  "font-e": fontE,
  "font-f": fontF,
};

const ICON_IMAGES: Record<string, string> = {
  "heart": iconHeart,
  "crown": iconCrown,
  "star": iconStar,
  "david": iconDavid,
  "ampersand": iconAmpersand,
};

interface EmptyCartCategory {
  handle: string;
  title: string;
}

const FREE_SHIPPING_THRESHOLD = 500;
const CHECKOUT_LANG_STORAGE_KEY = 'cart-checkout-language';

// Language options for checkout with translations
const checkoutLanguages = [
  { code: 'he', label: 'עברית', flag: flagIsrael, dir: 'rtl' as const },
  { code: 'en', label: 'English', flag: flagUSA, dir: 'ltr' as const },
  { code: 'ar', label: 'العربية', flag: flagPalestine, dir: 'rtl' as const },
  { code: 'ru', label: 'Русский', flag: flagRussia, dir: 'ltr' as const },
];

// Cart drawer translations
const cartTranslations = {
  he: {
    title: 'סל קניות',
    emptyTitle: 'הסל שלך ריק',
    emptyDescription: 'התחל לקנות ומצא את התכשיט המושלם עבורך',
    startShopping: 'התחל קנייה',
    freeShippingAchieved: 'הזמנתך זכאית למשלוח חינם',
    freeShippingRemaining: (remaining: number) => `חסרים לך עוד ₪${Math.round(remaining).toLocaleString('he-IL')} למשלוח חינם`,
    total: 'סה"כ:',
    checkoutLanguage: 'שפת התשלום:',
    checkout: 'המשך לתשלום',
    checkoutLoading: 'מעבר לתשלום...',
    recommendations: 'אולי תאהבו גם',
    engraving: 'חריטה:',
    engravingN: (n: number) => `חריטה ${n}:`,
    font: 'פונט:',
    iconSingular: 'סמל:',
    iconPlural: 'סמלים:',
    includesIcons: (count: number, price: number) => `כולל ${count} ${count === 1 ? 'סמל' : 'סמלים'} (+₪${price})`,
  },
  en: {
    title: 'Shopping Cart',
    emptyTitle: 'Your cart is empty',
    emptyDescription: 'Start shopping and find your perfect jewelry',
    startShopping: 'Start Shopping',
    freeShippingAchieved: 'Your order qualifies for free shipping',
    freeShippingRemaining: (remaining: number) => `You're ₪${Math.round(remaining).toLocaleString('en-US')} away from free shipping`,
    total: 'Total:',
    checkoutLanguage: 'Checkout Language:',
    checkout: 'Proceed to Checkout',
    checkoutLoading: 'Redirecting to checkout...',
    recommendations: 'You may also like',
    engraving: 'Engraving:',
    engravingN: (n: number) => `Engraving ${n}:`,
    font: 'Font:',
    iconSingular: 'Icon:',
    iconPlural: 'Icons:',
    includesIcons: (count: number, price: number) => `Includes ${count} ${count === 1 ? 'icon' : 'icons'} (+₪${price})`,
  },
  ar: {
    title: 'سلة التسوق',
    emptyTitle: 'سلتك فارغة',
    emptyDescription: 'ابدأ التسوق واعثر على المجوهرات المثالية لك',
    startShopping: 'ابدأ التسوق',
    freeShippingAchieved: 'طلبك مؤهل للشحن المجاني',
    freeShippingRemaining: (remaining: number) => `ينقصك ₪${Math.round(remaining).toLocaleString('ar-EG')} للشحن المجاني`,
    total: 'المجموع:',
    checkoutLanguage: 'لغة الدفع:',
    checkout: 'متابعة الدفع',
    checkoutLoading: 'جاري التحويل...',
    recommendations: 'قد يعجبك أيضاً',
    engraving: 'نقش:',
    engravingN: (n: number) => `نقش ${n}:`,
    font: 'خط:',
    iconSingular: 'رمز:',
    iconPlural: 'رموز:',
    includesIcons: (count: number, price: number) => `يشمل ${count} ${count === 1 ? 'رمز' : 'رموز'} (+₪${price})`,
  },
  ru: {
    title: 'Корзина',
    emptyTitle: 'Ваша корзина пуста',
    emptyDescription: 'Начните покупки и найдите идеальное украшение для себя',
    startShopping: 'Начать покупки',
    freeShippingAchieved: 'Ваш заказ получает бесплатную доставку',
    freeShippingRemaining: (remaining: number) => `До бесплатной доставки осталось ₪${Math.round(remaining).toLocaleString('ru-RU')}`,
    total: 'Итого:',
    checkoutLanguage: 'Язык оплаты:',
    checkout: 'Перейти к оплате',
    checkoutLoading: 'Переход к оплате...',
    recommendations: 'Вам также может понравиться',
    engraving: 'Гравировка:',
    engravingN: (n: number) => `Гравировка ${n}:`,
    font: 'Шрифт:',
    iconSingular: 'Символ:',
    iconPlural: 'Символы:',
    includesIcons: (count: number, price: number) => `Включает ${count} ${count === 1 ? 'символ' : 'символов'} (+₪${price})`,
  },
};

export const CartDrawer = () => {
  const { items, updateQuantity, removeItem, createCheckout, isLoading, getTotalPrice, getTotalItems, isCartOpen, setCartOpen } = useCartStore();
  const { locale, t, direction } = useLanguage();
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [emptyCartCategories, setEmptyCartCategories] = useState<EmptyCartCategory[]>([]);
  
  // Initialize from localStorage with Hebrew as default
  const [selectedCheckoutLang, setSelectedCheckoutLang] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CHECKOUT_LANG_STORAGE_KEY) || 'he';
    }
    return 'he';
  });

  // Load empty cart categories from settings
  useEffect(() => {
    const loadEmptyCartCategories = async () => {
      try {
        // Get the saved category handles from site_settings
        const { data: setting } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'empty_cart_categories')
          .maybeSingle();

        if (!setting?.setting_value) return;

        const value = setting.setting_value as { handles?: string[] };
        const handles = value.handles || [];
        
        if (handles.length === 0) return;

        // Fetch collection titles from Shopify
        const { collections } = await fetchShopifyCollections(250);
        
        // Map handles to categories, using the title if found or a formatted handle as fallback
        const categoriesWithTitles = handles.map((handle) => {
          const collection = collections.find(c => c.node.handle === handle);
          if (collection) {
            return { handle, title: collection.node.title };
          }
          // Fallback: format handle as readable title
          const formattedTitle = handle
            .replace(/-/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
          return { handle, title: formattedTitle };
        });

        setEmptyCartCategories(categoriesWithTitles);
      } catch (error) {
        console.error('Error loading empty cart categories:', error);
      }
    };

    loadEmptyCartCategories();
  }, []);

  // Save to localStorage when language changes
  const handleLanguageChange = (langCode: string) => {
    setSelectedCheckoutLang(langCode);
    localStorage.setItem(CHECKOUT_LANG_STORAGE_KEY, langCode);
  };

  // Get current translations and direction based on selected checkout language
  const cartT = cartTranslations[selectedCheckoutLang as keyof typeof cartTranslations] || cartTranslations.he;
  const cartDir = checkoutLanguages.find(l => l.code === selectedCheckoutLang)?.dir || 'rtl';

  const totalPrice = getTotalPrice();
  const progress = Math.min((totalPrice / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const remaining = Math.max(FREE_SHIPPING_THRESHOLD - totalPrice, 0);
  const hasFreeShipping = totalPrice >= FREE_SHIPPING_THRESHOLD;

  // Animate progress bar when cart opens (RTL: fills from right to left)
  useEffect(() => {
    if (isCartOpen) {
      setAnimatedProgress(0);
      const timer = setTimeout(() => {
        setAnimatedProgress(progress);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCartOpen, progress]);

  // Track crossing the free-shipping threshold so confetti only fires once per "cart fill" cycle
  const prevHasFreeShippingRef = useRef<boolean>(hasFreeShipping);

  // Confetti: fire once when progress bar animation reaches 100% after crossing threshold
  useEffect(() => {
    // If cart is empty, reset and prepare for next cycle
    if (items.length === 0) {
      setHasTriggeredConfetti(false);
      prevHasFreeShippingRef.current = false;
      return;
    }

    const prevHasFreeShipping = prevHasFreeShippingRef.current;
    prevHasFreeShippingRef.current = hasFreeShipping;

    // Fire only on transition: false -> true (and only while cart is open)
    // Wait for the progress bar animation to complete (1000ms duration + 100ms delay)
    if (isCartOpen && hasFreeShipping && !prevHasFreeShipping && !hasTriggeredConfetti) {
      const confettiTimer = setTimeout(() => {
        setHasTriggeredConfetti(true);

        const colors = ["#a99670", "#d4c4a8", "#8b7355", "#c9b896"];
        const end = Date.now() + 2000;

        const frame = () => {
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors,
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors,
          });

          if (Date.now() < end) requestAnimationFrame(frame);
        };

        frame();
      }, 1100); // Wait for animation to complete

      return () => clearTimeout(confettiTimer);
    }
  }, [isCartOpen, hasFreeShipping, items.length, hasTriggeredConfetti]);

  const handleCheckout = async () => {
    try {
      await createCheckout(selectedCheckoutLang);
    } catch (error) {
      console.error('Checkout error:', error);
    }
  };


  return (
    <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="hover:bg-transparent relative" aria-label="סל קניות">
          <ShoppingBag className="h-5 w-5 text-primary" strokeWidth={1.5} />
          {getTotalItems() > 0 && (
            <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {getTotalItems()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-3xl p-0" dir={cartDir}>
        <div className="flex flex-col h-full p-4 sm:p-6">
          <SheetHeader className={cartDir === 'rtl' ? 'text-right' : 'text-left'}>
            <SheetTitle>{cartT.title}</SheetTitle>
          </SheetHeader>
        
          <div className="flex-1 min-h-0 py-4">
            {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-center overflow-y-auto">
              <div className="space-y-6 px-4 w-full">
                <ShoppingBag className="h-20 w-20 text-primary/40 mx-auto" strokeWidth={1} />
                <div className="space-y-2">
                  <h3 className="text-xl" style={{ fontWeight: 300 }}>{cartT.emptyTitle}</h3>
                  <p className="text-sm text-muted-foreground" style={{ fontWeight: 300 }}>
                    {cartT.emptyDescription}
                  </p>
                </div>
                
                {/* Category buttons */}
                {emptyCartCategories.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {emptyCartCategories.map((category, index) => (
                      <Link 
                        key={category.handle} 
                        to={`/collections/${category.handle}`}
                        onClick={() => setCartOpen(false)}
                        className="group relative overflow-hidden border border-primary/30 rounded-lg py-3 px-4 text-sm font-medium text-foreground transition-all duration-300 hover:border-primary opacity-0 animate-fade-in-item"
                        style={{
                          animationDelay: `${index * 50}ms`,
                        }}
                      >
                        {/* Animated background fill from center */}
                        <span className="absolute inset-0 bg-primary/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-center" />
                        <span className="relative z-10">{category.title}</span>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Divider between categories and start shopping */}
                {emptyCartCategories.length > 0 && (
                  <div className="w-full h-px bg-border/50 my-2" />
                )}

                <Link to="/collections/all" className="block" onClick={() => setCartOpen(false)}>
                  <Button 
                    variant="outline" 
                    className="w-full border-primary/20 hover:bg-primary/10 hover:border-primary/40 transition-all duration-300 text-foreground hover:text-foreground py-6"
                  >
                    {cartT.startShopping}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full min-h-0">
              {/* Desktop: two-column layout with independent scrolling */}
              <div className="hidden md:grid grid-cols-5 gap-0 flex-1 min-h-0">
                {/* Cart items column - independent scroll */}
                <div className="col-span-3 overflow-y-auto min-h-0 pe-4 flex flex-col cart-app-scrollbar" style={{ overscrollBehavior: 'contain' }}>
                  {/* Free Shipping Progress Bar */}
                  <div className="flex-shrink-0 mb-3 p-4 bg-primary/5 border border-primary/10 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {hasFreeShipping ? (
                        <Gift className="h-5 w-5 text-primary flex-shrink-0" />
                      ) : (
                        <Truck className="h-5 w-5 text-primary/70 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium text-foreground">
                        {hasFreeShipping ? cartT.freeShippingAchieved : cartT.freeShippingRemaining(remaining)}
                      </span>
                    </div>
                    <div className="relative h-2 bg-primary/10 rounded-full overflow-hidden">
                      <div className={`absolute inset-y-0 rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-primary/70 to-primary ${cartDir === 'rtl' ? 'right-0' : 'left-0'}`} style={{ width: `${animatedProgress}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                      <span>₪0</span>
                      <span>₪{FREE_SHIPPING_THRESHOLD}</span>
                    </div>
                  </div>
                  {/* Cart items */}
                  <div className="space-y-3">
                    {items.map((item, index) => {
                      const displayImage = item.variantImage?.url || item.product.images?.edges?.[0]?.node.url;
                      const displayAlt = item.variantImage?.altText || item.product.title;
                      const itemKey = useCartStore.getState().getItemKey(item);
                      return (
                        <div key={itemKey} className="border-b border-border/50 pb-3 animate-fade-in-item opacity-0" style={{ animationDelay: `${index * 80}ms` }}>
                          <div className="flex gap-3">
                            <div className="w-20 h-20 bg-muted overflow-hidden flex-shrink-0 rounded">
                              {displayImage && <img src={displayImage} alt={displayAlt} loading="lazy" className="w-full h-full object-cover" />}
                            </div>
                            <div className={`flex-1 min-w-0 ${cartDir === 'rtl' ? 'text-right' : 'text-left'}`}>
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-sm leading-tight truncate">{item.product.title}</h3>
                                  <p className="text-xs text-muted-foreground mt-0.5">{item.variantTitle}</p>
                                </div>
                                <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 text-muted-foreground/50 hover:text-destructive hover:bg-transparent" onClick={() => removeItem(itemKey)}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              {(() => {
                                const ICON_PRICE = 60;
                                const basePrice = parseFloat(item.price.amount);
                                const iconsPrice = (item.icons?.length || 0) * ICON_PRICE;
                                const itemTotalPrice = basePrice + iconsPrice;
                                return (
                                  <div className="mt-1">
                                    <p className="text-primary font-semibold text-sm">{formatPrice(itemTotalPrice.toString(), item.price.currencyCode)}</p>
                                    {item.icons && item.icons.length > 0 && (
                                      <p className="text-xs text-muted-foreground">{cartT.includesIcons(item.icons.length, iconsPrice)}</p>
                                    )}
                                  </div>
                                );
                              })()}
                              <div className={`flex items-center gap-2 mt-2 ${cartDir === 'rtl' ? 'justify-end' : 'justify-start'}`}>
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(itemKey, item.quantity + 1)}><Plus className="h-3 w-3" /></Button>
                                <span className="text-sm w-8 text-center">{item.quantity}</span>
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(itemKey, item.quantity - 1)}><Minus className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          </div>
                          {(item.engraving || (item.icons && item.icons.length > 0)) && (
                            <div className="mt-2 py-2 px-4 border border-primary/20 bg-primary/[0.02]">
                              <div className="flex items-center justify-center gap-4 text-xs flex-wrap">
                                {item.engraving && (
                                  <>
                                    {(item.engraving.texts && item.engraving.texts.length > 0) ? (
                                      item.engraving.texts.map((text, idx) => (
                                        <span key={idx} className="flex items-center gap-1">
                                          <span className="text-muted-foreground">{item.engraving!.texts.length > 1 ? cartT.engravingN(idx + 1) : cartT.engraving}</span>
                                          <span className="text-foreground">{text}</span>
                                        </span>
                                      ))
                                    ) : (item.engraving as any).text ? (
                                      <span className="flex items-center gap-1">
                                        <span className="text-muted-foreground">{cartT.engraving}</span>
                                        <span className="text-foreground">{(item.engraving as any).text}</span>
                                      </span>
                                    ) : null}
                                    <span className="text-primary/30">|</span>
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-muted-foreground">{cartT.font}</span>
                                      {FONT_IMAGES[item.engraving.font] ? (
                                        <img src={FONT_IMAGES[item.engraving.font]} alt={item.engraving.font} className="h-5 w-auto object-contain" />
                                      ) : (
                                        <span className="text-foreground">{item.engraving.font}</span>
                                      )}
                                    </span>
                                  </>
                                )}
                                {item.icons && item.icons.length > 0 && (
                                  <>
                                    {item.engraving && <span className="text-primary/30">|</span>}
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-muted-foreground">{item.icons.length === 1 ? cartT.iconSingular : cartT.iconPlural}</span>
                                      <span className="flex items-center gap-1">
                                        {item.icons.map((iconId, idx) => (
                                          ICON_IMAGES[iconId] ? (
                                            <img key={idx} src={ICON_IMAGES[iconId]} alt={iconId} className="h-5 w-5 object-contain" />
                                          ) : (
                                            <span key={idx} className="text-foreground">{iconId}</span>
                                          )
                                        ))}
                                      </span>
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Recommendations column - independent scroll, border from top to bottom */}
                <div className="col-span-2 border-s border-e border-t border-border/50 ps-4 pe-4 pt-3 overflow-y-auto min-h-0 cart-app-scrollbar" style={{ overscrollBehavior: 'contain' }}>
                  <CartRecommendations cartItems={items} title={cartT.recommendations} />
                </div>
              </div>

              {/* Mobile: resizable split layout */}
              <MobileResizableCart
                items={items}
                cartDir={cartDir}
                cartT={cartT}
                hasFreeShipping={hasFreeShipping}
                remaining={remaining}
                animatedProgress={animatedProgress}
                removeItem={removeItem}
                updateQuantity={updateQuantity}
                FONT_IMAGES={FONT_IMAGES}
                ICON_IMAGES={ICON_IMAGES}
              />

              {/* Fixed bottom: Total + Checkout */}
              <div className="border-t pt-3 sm:pt-4 space-y-3 sm:space-y-4 flex-shrink-0 bg-background">
                <div className="flex justify-between text-base sm:text-lg font-semibold">
                  <span>{cartT.total}</span>
                  <span className="text-primary">
                    {items[0] && formatPrice(getTotalPrice().toString(), items[0].price.currencyCode)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">{cartT.checkoutLanguage}</span>
                  <TooltipProvider delayDuration={100}>
                    <div className="flex gap-1">
                      {checkoutLanguages.map((lang) => (
                        <Tooltip key={lang.code}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleLanguageChange(lang.code)}
                              className={`p-1 rounded-md border-2 transition-all duration-200 ${
                                selectedCheckoutLang === lang.code
                                  ? 'border-primary bg-primary/10'
                                  : 'border-transparent hover:border-primary/30'
                              }`}
                            >
                              <img src={lang.flag} alt={lang.label} className="w-6 h-4 sm:w-7 sm:h-5 object-cover rounded-sm" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{lang.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </TooltipProvider>
                </div>

                <Button className="w-full" size="lg" onClick={handleCheckout} disabled={isLoading}>
                  {isLoading ? cartT.checkoutLoading : cartT.checkout}
                </Button>
              </div>
            </div>
          )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
